export default {
  index: {
    title: '조석과 조류 안내서',
    description:
      '조석과 조류의 원리를 알아보세요: 조석 계수, 밀물 조류와 썰물 조류, 조석 기준면, 그리고 조석 예측이 어떻게 계산되는지.',
    subtitle: '조석과 조류 예측의 바탕이 되는 과학을 짧고 쉬운 말로 풀어 설명합니다.',
  },
  back: '모든 안내서로 돌아가기',
  guides: {
    'tidal-coefficients': {
      title: '조석 계수 이해하기',
      description:
        '조석 계수의 의미: 20에서 120까지의 척도, 사리와 조금이 달과 어떻게 관련되는지, 그리고 계수가 높을수록 조차가 커지는 이유.',
      blurb: '조석의 크기를 요약하는 20에서 120까지의 척도, 그리고 그것을 이끄는 달의 작용.',
      body: [
        {
          p: '조석 계수는 특정한 날의 조석이 얼마나 클지를 하나의 숫자로 요약한 값입니다. 프랑스와 프랑스 수로 측량 전통을 따르는 여러 나라에서 널리 사용됩니다. 이 계수는 무차원이며 보통 약 20에서 120까지의 범위를 가집니다. 숫자가 클수록 만조와 간조 사이의 차이(조차)가 커지고, 그에 따른 조류도 강해집니다.',
        },
        { h2: '20에서 120까지의 척도' },
        {
          p: '이 계수는 그날의 조차를 장기 기준과 비교한 것입니다. 척도상의 몇몇 값에는 관례적인 의미가 있습니다:',
        },
        {
          list: [
            { term: '20', text: '가장 작은 조석으로, 천문학적 조차의 최솟값 부근.' },
            { term: '45', text: '평균적인 조금으로, 조차가 작을 때.' },
            { term: '70', text: '평균 조석으로, 조금과 사리의 대략 중간.' },
            { term: '95', text: '평균적인 사리로, 조차가 클 때.' },
            { term: '120', text: '가장 큰 조석으로, 천문학적 조차의 최댓값 부근.' },
          ],
        },
        { h2: '계수가 변하는 이유: 달' },
        {
          p: '계수는 약 2주 주기의 사리-조금 주기에 따라 오르내립니다. 계수가 높은 사리는 삭(신월)과 망(보름)이 지난 뒤 하루나 이틀 후에 나타나며, 이때 태양과 달이 일직선으로 늘어서 두 천체의 기조력이 서로 더해집니다. 계수가 낮은 조금은 상현과 하현 무렵에 나타나며, 이때 태양과 달이 직각을 이루어 서로의 힘이 부분적으로 상쇄됩니다.',
        },
        {
          p: '달의 거리도 중요합니다. 사리가 근지점(달이 지구에 가장 가까이 다가오는 지점)과 겹치면 계수는 가장 높은 값에 이릅니다. 가장 큰 조석은 춘분과 추분 무렵에 몰리는 경향이 있습니다.',
        },
        { h2: '계수가 알려 주지 않는 것' },
        {
          p: '계수는 천문학적 조차만을 나타냅니다. 날씨의 영향은 포함하지 않습니다. 바람과 기압은 계수와 상관없이 실제 수위를 높이거나 낮출 수 있습니다. 또한 계수는 그날 조석 전체의 성질이지, 특정 장소에서의 높이(미터나 피트)가 아닙니다.',
        },
      ],
    },
    'tidal-currents': {
      title: '밀물, 썰물, 게류: 조류의 원리',
      description:
        '조류의 원리: 밀물과 썰물의 방향, 흐름이 바뀔 때의 게류, 노트로 나타내는 유속과 유향(set), 그리고 게류가 항상 만조·간조와 일치하지는 않는 이유.',
      blurb: '밀물 조류, 썰물 조류, 그리고 게류, 그리고 가장 강한 흐름이 항상 만조 때가 아닌 이유.',
      body: [
        {
          p: '조류는 조석의 수직적인 오르내림에 동반되는 물의 수평 방향 움직임입니다. 조위가 물의 깊이를 알려 준다면, 조류는 물이 어느 방향으로 얼마나 빠르게 흐르는지를 알려 줍니다. 조류는 수로, 입구(inlet), 하구에서 가장 중요하며, 그곳에서는 몇 노트에 이르기도 합니다.',
        },
        { h2: '밀물 조류, 썰물 조류, 게류' },
        {
          list: [
            {
              term: '밀물 조류',
              text: '육지 쪽으로, 또는 하구를 거슬러 흐르는 물. 일반적으로 조위가 오르는 동안.',
            },
            {
              term: '썰물 조류',
              text: '바다 쪽으로, 또는 하구를 따라 내려가며 흐르는 물. 일반적으로 조위가 내리는 동안.',
            },
            {
              term: '게류',
              text: '밀물과 썰물 사이에서 흐름이 방향을 바꿀 때, 유속이 거의 0에 가까워지는 짧은 멈춤.',
            },
          ],
        },
        {
          p: '유속은 노트(시간당 해리)로 측정합니다. 그 방향은 유향(set)이라고 하며, 물이 흘러가는 방향을 나침 방위로 나타냅니다.',
        },
        { h2: '게류가 항상 만조 때인 것은 아니다' },
        {
          p: '흔한 오해는 만조와 간조에서 흐름이 멈춘다는 것입니다. 그것이 맞는 곳은 일부 지역뿐입니다. 시점은 그 지역에서 조석파가 어떻게 작용하는지에 따라 달라집니다. 길게 트인 수로처럼 조석이 진행파처럼 작용하는 곳에서는 가장 강한 밀물과 썰물이 만조·간조 무렵에 일어나고, 게류는 그 중간 지점 부근에 옵니다. 닫힌 만의 안쪽처럼 정상파(정재파)처럼 작용하는 곳에서는 반대로, 흐름이 가장 빠른 때는 중간 조위 부근이고 게류는 만조·간조 무렵에 일어납니다. 믿을 수 있는 길잡이는 그 지역의 조류표입니다.',
        },
        { h2: '반전류와 회전류' },
        {
          p: '좁은 수로에서 흐름은 대개 반전류입니다. 밀물 때 한 방향으로 흐르다가 방향을 바꾸어 썰물 때 반대로 흐릅니다. 트인 연안 해역에서는 흐름이 오히려 회전류인 경우가 많아, 하나의 선을 따라 반전하는 대신 조석 주기 동안 나침 방위를 따라 방향을 연속적으로 바꿉니다.',
        },
      ],
    },
    'tidal-datums': {
      title: '조석 기준면과 조위',
      description:
        '조위가 무엇을 기준으로 측정되는지: 해도 기준면, 최저 천문 조위(LAT), 평균 저저조위(MLLW), 그리고 나라마다 기준면이 다른 이유.',
      blurb: '해도 기준면, LAT, MLLW, 그리고 조위 숫자가 실제로 의미하는 것.',
      body: [
        {
          p: '모든 조위는 기준면이라 불리는 기준 높이 위로의 측정값입니다. 조석표에서 물이 1.8미터에 이른다고 하면, 그것은 그 지역의 조석 기준면에서 1.8미터라는 뜻이지, 해저에서부터도 아니고 일상적인 의미의 해수면에서부터도 아닙니다. 기준면을 아는 것이 높이에 의미를 부여합니다.',
        },
        { h2: '조석 기준면이란' },
        {
          p: '조석 기준면은 조석의 특정 단계로 정의되는 기준 높이로, 오랜 기간에 걸쳐 평균한 값입니다. 관례적으로 약 19년인데, 이는 달 궤도의 18.6년 주기를 고르게 하기 위함입니다. 실제 관측값의 평균이므로, 기준면은 그 장소와 그것을 산출한 기관에 고유합니다.',
        },
        { h2: '해도 기준면과 일반적인 기준' },
        {
          p: '해도는 저조 기준면을 사용하므로 실제 수심은 거의 언제나 해도에 표시된 수심 이상이 됩니다. 이 해도 기준면이 해도 수심과 조위를 모두 재는 0점입니다. 나라마다 채택하는 기준면이 다릅니다:',
        },
        {
          list: [
            {
              term: 'LAT(최저 천문 조위)',
              text: '평균적인 날씨에서 예측되는 가장 낮은 수위. 영국을 비롯한 여러 나라에서 사용합니다.',
            },
            {
              term: 'MLLW(평균 저저조위)',
              text: '매일 두 번 있는 저조 중 낮은 쪽을 평균한 값. 미국의 표준 해도 기준면입니다.',
            },
            {
              term: 'MSL(평균 해수면)',
              text: '장기간에 걸친 평균 수위. 해도보다는 주로 육지 표고의 기준으로 사용됩니다.',
            },
          ],
        },
        {
          p: '평균 고조위(MHW)와 평균 고고조위(MHHW) 같은 고조 기준면은 반대의 목적으로 사용됩니다. 예를 들어 해안선을 정의하거나 교량의 통과 높이(clearance)를 나타내는 데 쓰입니다.',
        },
        { h2: '높이를 직접 비교할 수 없는 이유' },
        {
          p: '기준면이 다르므로, 한 관측소의 높이를 다른 관측소의 높이와 직접 비교할 수는 없으며, 둘이 같은 기준을 쓰는 경우에만 가능합니다. LAT에서 잰 0.5미터 저조는 MLLW에서 잰 0.5미터 저조와 같은 수위가 아닙니다. 같은 물리적 조석이 나라에 따라 다른 숫자로 표현되는 것도 이 때문입니다.',
        },
      ],
    },
    'how-tide-predictions-work': {
      title: '조석 예측은 어떻게 계산되는가',
      description:
        '조석이 어떻게 예측되는지: 달과 태양의 중력 작용, M2와 S2 같은 조화 분조, 그리고 관측된 수위가 천문 조석과 다른 이유.',
      blurb: '조화 분석, 조석을 이루는 분조, 그리고 날씨 때문에 현실이 달라지는 이유.',
      body: [
        {
          p: '조석은 여러 해 앞서 예측할 수 있습니다. 조석이 달과 태양의 운동에 의해 일어나고, 그 운동이 매우 정밀하게 알려져 있기 때문입니다. 어느 장소의 조석은 그 중력 작용에 대한 바다의 반응이며, 그 지역의 해안선과 수심에 의해 형태가 갖춰집니다. 천문학을 조석표로 바꾸는 방법을 조화 분석이라고 합니다.',
        },
        { h2: '분조의 합으로서의 조석' },
        {
          p: '조화 분석은 조석을 여러 개의 단순한 파동의 합으로 다루며, 그 하나하나를 분조라고 부릅니다. 각 분조의 주파수는 천문학에 의해 고정됩니다. 즉 지구, 달, 태양이 서로에 대해 어떻게 움직이는지에 따라 정해집니다. 반면 그 진폭과 시점은 그 장소에서 실제로 관측한 일정 기간의 수위에 분조를 맞추어 구합니다. 주요 분조로는 다음이 있습니다:',
        },
        {
          list: [
            { term: 'M2', text: '주태음 반일주조. 주기 12.42시간으로, 보통 가장 크다.' },
            { term: 'S2', text: '주태양 반일주조. 주기 12.00시간.' },
            { term: 'N2', text: '달의 타원 궤도에서 비롯되는 태음 분조.' },
            {
              term: 'K1 and O1',
              text: '주요 일주조로, 많은 곳에서 하루 두 번의 만조 중 하나를 다른 하나보다 크게 만든다.',
            },
          ],
        },
        {
          p: '익숙한 사리-조금 주기는 여기서 자연스럽게 나타납니다. M2와 S2는 주기가 조금 달라서 서로 위상이 어긋났다 맞았다 하며, 14.8일마다 강해져 사리를 이루고 부분적으로 상쇄되어 조금을 이룹니다.',
        },
        { h2: '앞으로의 예측' },
        {
          p: '어느 장소에 대해 각 분조의 진폭과 위상을 알고 나면, 조석 예측은 임의의 미래 시각에 그 합을 계산하는 것에 지나지 않습니다. 천문학적 주파수는 결코 변하지 않으므로 계산은 결정론적이며, 수십 년 앞까지 수행할 수 있습니다.',
        },
        { h2: '실제 조석이 다른 이유' },
        {
          p: '예측은 천문 조석일 뿐입니다. 날씨는 포함하지 않습니다. 강한 바람, 낮거나 높은 기압, 폭우, 하천 유입은 실제 수위를 높이거나 낮출 수 있습니다. 폭풍 해일은 예측보다 1미터 이상 수위를 더할 수 있고, 반대로 고기압과 앞바다로 부는 바람은 수위를 예측보다 훨씬 낮게 붙잡아 둘 수 있습니다. 따라서 예측은 조석의 시점과 크기를 아는 데 매우 훌륭한 길잡이이지만, 그날 관측되는 수위는 다를 수 있습니다.',
        },
      ],
    },
  },
}
